import { useCallback, useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import data3dprint from '../config/data3dprint.json';

export interface FormValues {
  technology: string;
  printer: number | undefined;
  material: number | undefined;
  color: number | undefined;
  infill: number | undefined;
  quality: number | undefined;
  additionalServices: number | undefined;
}

interface PrintFormHandlers {
  handleTechnologyChange: (value: string) => void;
  handlePrinterChange: (value: string) => void;
  handleMaterialChange: (value: string) => void;
  handleColorChange: (value: string) => void;
  handleInfillChange: (value: string) => void;
  handleQualityChange: (value: string) => void;
  handleAdditionalServicesChange: (value: string) => void;
}

export function usePrintForm(volumeCm3: number, onCalculatePrice: (total: number) => void) {
  const { control, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      technology: '',
      printer: undefined,
      material: undefined,
      color: undefined,
      infill: undefined,
      quality: undefined,
      additionalServices: 0
    }
  });

  const formValues = watch();

  // Estados locais
  const [activeField, setActiveField] = useState<string>('technology');
  const [selectedTechnology, setSelectedTechnology] = useState<string>('');
  const [selectedPrinter, setSelectedPrinter] = useState<number | undefined>(undefined);
  const [selectedMaterial, setSelectedMaterial] = useState<number | undefined>(undefined);
  const [selectedColor, setSelectedColor] = useState<number | undefined>(undefined);
  const [selectedInfill, setSelectedInfill] = useState<number | undefined>(undefined);
  const [selectedQuality, setSelectedQuality] = useState<number | undefined>(undefined);
  const [lastCalculatedPrice, setLastCalculatedPrice] = useState<number>(0);

  // Dados selecionados memoizados
  const selectedData = useMemo(() => {
    const techData = data3dprint.technologies.find(t => t.name === selectedTechnology);
    const printerData = techData?.printers.find(p => p.id === selectedPrinter);
    const materialData = printerData?.materials.find(m => m.id === selectedMaterial);

    return {
      technology: techData,
      printer: printerData,
      material: materialData
    };
  }, [selectedTechnology, selectedPrinter, selectedMaterial]);

  // Handlers memoizados
  const handlers = useMemo<PrintFormHandlers>(() => ({
    handleTechnologyChange: (value: string) => {
      setSelectedTechnology(value);
      setValue('technology', value);
      setActiveField('printer');
    },

    handlePrinterChange: (value: string) => {
      const printerId = Number(value);
      setSelectedPrinter(printerId);
      setValue('printer', printerId);
      setActiveField('material');
    },

    handleMaterialChange: (value: string) => {
      const materialId = Number(value);
      setSelectedMaterial(materialId);
      setValue('material', materialId);
      setActiveField('color');
    },

    handleColorChange: (value: string) => {
      const colorId = Number(value);
      setSelectedColor(colorId);
      setValue('color', colorId);
      setActiveField('infill');
    },

    handleInfillChange: (value: string) => {
      const infillId = Number(value);
      setSelectedInfill(infillId);
      setValue('infill', infillId);
      setActiveField('quality');
    },

    handleQualityChange: (value: string) => {
      const qualityId = Number(value);
      setSelectedQuality(qualityId);
      setValue('quality', qualityId);
      setActiveField('');
    },

    handleAdditionalServicesChange: (value: string) => {
      const serviceId = value ? Number(value) : undefined;
      if (formValues.additionalServices === serviceId) return;
      setValue('additionalServices', serviceId);
    }
  }), [setValue]);

  // Pré-selecionar o primeiro serviço adicional quando disponível
  useEffect(() => {
    if (selectedQuality && selectedPrinter) {
      const technology = data3dprint.technologies.find(
        tech => tech.name === selectedTechnology
      );
      const printer = technology?.printers.find(
        p => p.id === selectedPrinter
      );
      
      if (printer && printer.additional_services?.length > 0) {
        setValue('additionalServices', printer.additional_services[0].id);
      }
    }
  }, [selectedQuality, selectedPrinter, selectedTechnology, setValue]);

  // Cálculo de preço memoizado
  const calculatePrice = useCallback(() => {
    // Early return se não tiver tecnologia ou impressora
    if (!formValues.technology || !formValues.printer) {
      onCalculatePrice(0);
      return;
    }

    let totalPrice = 0;

    // Adiciona serviços adicionais se houver
    if (formValues.additionalServices && selectedData.printer?.additional_services) {
      const service = selectedData.printer.additional_services.find(
        s => s.id === formValues.additionalServices
      );
      if (service) {
        totalPrice += service.price;
      }
    }

    // Early return se faltar dados essenciais
    if (!formValues.material || !formValues.color || !formValues.quality) {
      if (totalPrice !== lastCalculatedPrice) {
        setLastCalculatedPrice(totalPrice);
        onCalculatePrice(totalPrice);
      }
      return;
    }

    try {
      const material = selectedData.material;
      const variant = material?.variants[0];
      const color = variant?.colors.find(c => c.id === formValues.color);
      const quality = selectedData.printer?.nozzle_sizes[0].quality_options.find(
        q => q.id === formValues.quality
      );
      const infill = selectedData.printer?.infill_options.find(
        i => i.id === formValues.infill
      );

      if (!material || !color || !quality || !variant) {
        if (totalPrice !== lastCalculatedPrice) {
          setLastCalculatedPrice(totalPrice);
          onCalculatePrice(totalPrice);
        }
        return;
      }

      // Cálculo do material
      const materialCost = formValues.technology === 'FDM'
        ? volumeCm3 * variant.density * (variant.base_price / variant.spool_weight)
        : volumeCm3 * (variant.base_price / variant.spool_weight);

      let basePrice = materialCost * color.price_multiplier;

      if (formValues.technology === 'FDM' && infill) {
        basePrice *= infill.material_multiplier;
        basePrice *= infill.time_multiplier;
      }

      basePrice *= quality.time_multiplier;
      totalPrice += Math.round(basePrice * 100) / 100;

      if (totalPrice !== lastCalculatedPrice) {
        setLastCalculatedPrice(totalPrice);
        onCalculatePrice(totalPrice);
      }
    } catch (error) {
      console.error('Erro no cálculo do preço:', error);
      onCalculatePrice(0);
    }
  }, [formValues, selectedData, volumeCm3, onCalculatePrice]);

  useEffect(() => {
    calculatePrice();
  }, [calculatePrice]);

  const isFieldActive = useCallback((fieldName: string) => {
    return activeField === fieldName;
  }, [activeField]);

  const prepareServices = () => {
    if (!selectedTechnology || !selectedPrinter) return null;

    const technology = data3dprint.technologies.find(t => t.name === selectedTechnology);
    const printer = technology?.printers.find(p => p.id === selectedPrinter);
    const material = printer?.materials.find(m => m.id === selectedMaterial);
    const variant = material?.variants[0];
    const color = variant?.colors.find(c => c.id === selectedColor);
    const infill = printer?.infill_options.find(i => i.id === selectedInfill);
    const quality = printer?.nozzle_sizes[0]?.quality_options.find(q => q.id === selectedQuality);
    const additionalService = printer?.additional_services?.find(s => s.id === formValues.additionalServices);

    return {
      technology: technology?.name || '',
      printer: printer?.name || '',
      material: material?.type || '',
      color: color?.name || '',
      infill: infill?.percentage ? `${infill.percentage}%` : '',
      quality: quality ? `${quality.label} - ${quality.layer_height}mm` : '',
      additionalService: additionalService?.label || ''
    };
  };

  return {
    control,
    formValues,
    selectedData,
    handlers,
    calculatePrice,
    selectedTechnology,
    selectedPrinter,
    selectedMaterial,
    selectedColor,
    selectedInfill,
    selectedQuality,
    isFieldActive,
    prepareServices
  };
}